import React, { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faPlus,
  faFileExcel,
  faFileImport,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";

import { useNavigate } from "react-router-dom";
import "./ControlAlimentos.css"; // Add a separate CSS file for styles
import { useShoppingContext } from "../../../context/ShoppingContext";

const ControlAlimentos = () => {
  const {
    controlAlimentos,
    setControlAlimentos,
    alimentos,
    setAlimentos,
    setShowAddControlAlimentos,
    fetchAlimentos,
    getAveNombre,
    getAlimentoNombre,
    renderDateTitle,
    selectedAve,
  } = useShoppingContext();

  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3; // Número de elementos por página

  // Filtrar los controles de alimentos según el ave seleccionada
  const filteredControlAlimentos = selectedAve
    ? controlAlimentos.filter((control) => control.ave_id === selectedAve.id)
    : controlAlimentos;

  // Ordenar los registros por fecha
  const sortedControlAlimentos = [...filteredControlAlimentos].sort(
    (a, b) => new Date(b.fecha) - new Date(a.fecha)
  );

  // Cálculo del total de páginas
  const totalPages = Math.ceil(sortedControlAlimentos.length / itemsPerPage);

  // Manejar el cambio de página
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Datos de la página actual
  const paginatedData = sortedControlAlimentos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Renderizar la paginación
  const renderPagination = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <span
          key={i}
          className={`pagination-item ${
            currentPage === i ? "active" : ""
          }`} // Clase activa para la página seleccionada
          onClick={() => handlePageChange(i)}
        >
          {i}
        </span>
      );
    }

    return (
      <div className="pagination-container">
        {currentPage > 1 && (
          <span
            className="pagination-arrow"
            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
          >
            {"<"}
          </span>
        )}
        {pageNumbers}
        {currentPage < totalPages && (
          <span
            className="pagination-arrow"
            onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
          >
            {">"}
          </span>
        )}
        
        <span className="pagination-info">
            Página {currentPage} de {totalPages}
          </span>
      </div>
    );
  };

  // Exportar datos a Excel
  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const data = sortedControlAlimentos.map((control) => ({
      Lote: getAveNombre(control.ave_id),
      Fecha: renderDateTitle(control.fecha),
      Alimento: getAlimentoNombre(control.id_alimento),
      "Total Consumido (Kg)": control.ingesta_diaria - control.sobra,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Control de Alimentos");
    XLSX.writeFile(workbook, "control_alimentos.xlsx");
  };

  const handleDeleteAll = () => {
    if (
      window.confirm(
        "¿Estás seguro de que deseas eliminar todos los registros de control de alimentos?"
      )
    ) {
      axios
        .delete(`${process.env.REACT_APP_API_URL}/control-alimentos`)
        .then(() => {
          setControlAlimentos([]); // Limpiar el estado después de la eliminación
        })
        .catch((error) => {
          console.error("Error al eliminar los registros: ", error);
        });
    }
  };

  const handleDelete = (id) => {
    const control = controlAlimentos.find((c) => c.id === id);
    const alimento = alimentos.find((a) => a.id === control.id_alimento);

    if (
      window.confirm(
        `¿Está seguro de que desea eliminar este registro de consumo de alimentos para el lote ${getAveNombre(
          control.ave_id
        )}?`
      )
    ) {
      axios
        .delete(`${process.env.REACT_APP_API_URL}/control-alimentos/${id}`)
        .then(() => {
          setControlAlimentos(
            controlAlimentos.filter((control) => control.id !== id)
          );
          const newCantidad = alimento.cantidad + control.ingesta_diaria;

          axios
            .put(`${process.env.REACT_APP_API_URL}/alimentos/${alimento.id}`, {
              cantidad: newCantidad,
            })
            .then(() => {
              setAlimentos(
                alimentos.map((a) =>
                  a.id === alimento.id ? { ...a, cantidad: newCantidad } : a
                )
              );
              fetchAlimentos();
            })
            .catch((error) =>
              console.error("Error al actualizar el alimento: ", error)
            );
        })
        .catch((error) =>
          console.error("Error al eliminar el registro: ", error)
        );
    }
  };

  return (
    <>
      {alimentos.length === 0 ? (
        <div>
          <button
            className="btn control-alimentos-btn btn-green"
            onClick={() => navigate("/configuracion")}
          >
            Añadir un tipo de alimento
          </button>
        </div>
      ) : (
        <div className="flex">
          <button
            onClick={() => setShowAddControlAlimentos(true)}
            className="btn control-action-btn btn-green"
          >
            Alimentar lote <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      )}

      {paginatedData.length === 0 ? (
        <></>
      ) : (
        <div className="control-secondary-btns">
          <button
            className="btn control-alimentos-btn btn-success me-2"
            onClick={exportToExcel}
          >
            <FontAwesomeIcon icon={faFileExcel} /> Export
          </button>
          <button
            className="btn control-alimentos-btn btn-danger"
            onClick={handleDeleteAll}
          >
            <FontAwesomeIcon icon={faTrash} /> All
          </button>
        </div>
      )}

      {!paginatedData.length ? (
        <div className="text-center color-white my-4">
          <p className="fw-bold">
            Aún no has registrado el consumo diario de alimentos.
          </p>
          <p className="">
            ¡Empieza a llevar un control preciso, optimiza la nutrición de tus
            aves y observa su progreso para maximizar la eficiencia de tu
            granja!
          </p>
        </div>
      ) : (
        <>
          <div className="control-cards mt-2">
            {paginatedData.map((control) => (
              <div key={control.id} className="control-card">
                <div className="card-header">
                  <h5>{renderDateTitle(control.fecha)}</h5>
                  <h6>Alimento: {getAlimentoNombre(control.id_alimento)}</h6>
                </div>
                <div className="card-body">
                  <p>
                    <strong>Total Consumido: </strong>
                    {control.ingesta_diaria - control.sobra} Kg
                  </p>
                </div>
                <div className="card-footer">
                  <button className="btn btn-warning me-2">
                    <FontAwesomeIcon icon={faEdit} /> Editar
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(control.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} /> Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="control-alimentos-pagination flex justify-content-center mt-2">
            {renderPagination()}
          </div>
        </>
      )}
    </>
  );
};

export default ControlAlimentos;
